import { useContext, useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { axiosClient } from '../../../library/Service/axiosClient';
import { toast } from 'react-toastify';
import SessionContext from '../../../library/Core/SessionContext';
import { SmartContext } from '../../../library/Core/SmartContext';
import { allowPerformAction, displayActionNotAllowedAlert, getActionElementClass, getDomainValueForCode } from '../../../library/Core/SmartFunctions';
import BusinessDirectoryService from './BusinessDirectoryService';

function ComposeEmailModal({ provider, className, iconMain, btnTitle }) {
    const { state } = useContext(SmartContext);
    const { sessionState } = useContext(SessionContext);
    const isSuperAdmin = sessionState?.role === 'SUPER_ADMIN' || sessionState?.userTypeCode === 'SUPER_ADMIN';
    const [show, setShow] = useState(false);
    const [resolvedProvider, setResolvedProvider] = useState(provider || {});
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateType, setSelectedTemplateType] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    // Each entry: { id, name, url, uploading, error }
    const [attachments, setAttachments] = useState([]);
    const isAnyUploading = attachments.some((a) => a.uploading);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
    const [isLoadingProvider, setIsLoadingProvider] = useState(false);

    const activeProvider = resolvedProvider || provider || {};

    const mapProviderDetail = (detail) => ({
        providerId: detail?.providerId || detail?.id || provider?.providerId || '',
        alumniName: detail?.providerName || provider?.alumniName || '',
        contactEmailId: detail?.email || provider?.contactEmailId || '',
        category:
            getDomainValueForCode(detail?.categoryCode, 'BUSINESS_CATEGORY', state)
            || detail?.categoryCode
            || provider?.category
            || '',
        subcategory:
            getDomainValueForCode(detail?.subcategoryCode, 'BUSINESS_SUBCATEGORY', state)
            || detail?.subcategoryCode
            || provider?.subcategory
            || '',
    });

    const getPlainTextLength = (html) => {
        if (!html) return 0;
        const div = document.createElement('div');
        div.innerHTML = html;
        return (div.textContent || div.innerText || '').replace(/\u00a0/g, ' ').trim().length;
    };

    const isDirty = subject.trim() !== '' || getPlainTextLength(message) > 0 || attachments.length > 0;
    const attachmentUrls = attachments.filter((a) => a.url).map((a) => a.url);

    useEffect(() => {
        setResolvedProvider(provider || {});
    }, [provider]);

    const getSessionUserMeta = () => {
        try {
            const raw = sessionStorage.getItem('user');
            const user = raw ? JSON.parse(raw) : {};
            const firstName = sessionState?.firstName || user?.firstName || user?.first_name || '';
            const lastName = sessionState?.lastName || user?.lastName || user?.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim()
                || user?.name
                || user?.userName
                || '';

            const primaryInstituteId =
                sessionState?.primaryInstitute
                || sessionState?.instituteId
                || user?.primaryInstitute
                || user?.instituteId
                || null;

            const instituteNameFromSession =
                sessionState?.instituteName
                || sessionState?.schoolName
                || sessionState?.schoolSetting?.name
                || sessionState?.institutes?.find((institute) => String(institute?.instituteId) === String(primaryInstituteId))?.instituteName
                || sessionState?.institutes?.[0]?.instituteName
                || user?.instituteName
                || user?.institute_name
                || user?.schoolName
                || '';

            const instituteNameFromDomain = primaryInstituteId
                ? getDomainValueForCode(`${primaryInstituteId}`, 'INSTITUTE_DOMAIN', state)
                : '';

            const schoolSettings = localStorage.getItem('school_settings');
            const parsedSchoolSettings = schoolSettings ? JSON.parse(schoolSettings) : {};

            const instituteName = instituteNameFromSession
                || (instituteNameFromDomain && instituteNameFromDomain !== `${primaryInstituteId}` ? instituteNameFromDomain : '')
                || parsedSchoolSettings?.name
                || '';

            const contactInfo = sessionState?.email
                || sessionState?.registeredEmailId
                || user?.email
                || user?.registeredEmailId
                || user?.mobileNumber
                || '';
            return { fullName, instituteName, contactInfo };
        } catch (e) {
            return { fullName: '', instituteName: '', contactInfo: '' };
        }
    };

    const applyTemplatePlaceholders = (input = '') => {
        const { fullName, instituteName, contactInfo } = getSessionUserMeta();

        const providerName = activeProvider?.alumniName || '';
        const category = activeProvider?.category || '';
        const subcategory = activeProvider?.subcategory || '';
        const categorySubcategory = [category, subcategory].filter(Boolean).join(' - ');

        const rules = [
            {
                value: providerName,
                patterns: [
                    /\[(provider[\s_-]*name|receiver[\s_-]*name)\]/gi,
                    /#(provider[\s_-]*name|receiver[\s_-]*name)#/gi,
                ],
            },
            {
                value: categorySubcategory,
                patterns: [
                    /\[service[\s_-]*category\s*[-/]\s*subcategory\]/gi,
                    /#service[\s_-]*category[\s_-]*subcategory#/gi,
                ],
            },
            {
                value: category,
                patterns: [
                    /\[service[\s_-]*category\]/gi,
                    /#service[\s_-]*category#/gi,
                ],
            },
            {
                value: subcategory,
                patterns: [
                    /\[service[\s_-]*subcategory\]/gi,
                    /\[subcategory\]/gi,
                    /#service[\s_-]*subcategory#/gi,
                    /#subcategory#/gi,
                ],
            },
            {
                value: fullName,
                patterns: [
                    /\[your[\s_-]*name\]/gi,
                    /#your[\s_-]*name#/gi,
                    /#your[\s_-]*name\b/gi,
                ],
            },
            {
                value: instituteName,
                patterns: [
                    /\[institute[\s_-]*name\]/gi,
                    /#institute[\s_-]*name#/gi,
                    /#institute[\s_-]*name\b/gi,
                ],
            },
            {
                value: contactInfo,
                patterns: [
                    /\[contact[\s_-]*information\]/gi,
                    /#contact[\s_-]*information#/gi,
                    /#contact[\s_-]*information\b/gi,
                ],
            },
        ];

        return rules.reduce((acc, rule) => {
            if (!rule.value) return acc;
            return rule.patterns.reduce((updated, pattern) => updated.replace(pattern, rule.value), acc);
        }, `${input || ''}`);
    };

    const loadTemplates = async () => {
        setIsLoadingTemplates(true);
        try {
            const response = await axiosClient().get(`${process.env.REACT_APP_COMMON_API}/email/email-templates`);
            setTemplates(response?.data?.templates || []);
        } catch (err) {
            console.error('Failed to load email templates:', err);
        } finally {
            setIsLoadingTemplates(false);
        }
    };

    const ensureProviderDetail = async () => {
        if (!provider?.providerId) return;
        if (provider?.contactEmailId && provider?.alumniName) return;

        setIsLoadingProvider(true);
        try {
            const response = await BusinessDirectoryService.getProviderDetail(provider.providerId);
            const detail = response?.data;
            if (detail) {
                setResolvedProvider((prev) => ({
                    ...prev,
                    ...mapProviderDetail(detail),
                }));
            }
        } catch (err) {
            console.error('Failed to load provider contact details:', err);
            toast.error('Failed to load provider contact details.');
        } finally {
            setIsLoadingProvider(false);
        }
    };

    const handleShow = async () => {
        if (!allowPerformAction()) {
            displayActionNotAllowedAlert();
            return;
        }
        if (isSuperAdmin) {
            toast.info('Super Admin cannot send emails from Business Directory.');
            return;
        }
        setShow(true);
        loadTemplates();
        ensureProviderDetail();
    };

    const handleClose = () => {
        if (isDirty) {
            if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) return;
        }
        resetForm();
        setShow(false);
    };

    const resetForm = () => {
        setSelectedTemplateType('');
        setSubject('');
        setMessage('');
        setAttachments([]);
        setErrors({});
        setIsSubmitting(false);
    };

    const handleTemplateChange = async (e) => {
        const type = e.target.value;

        if (!type) {
            setSelectedTemplateType('');
            return;
        }

        if (subject.trim() || message.trim()) {
            if (!window.confirm('This will replace the current subject and message with the selected template. Continue?')) {
                return;
            }
        }

        setSelectedTemplateType(type);
        try {
            const response = await axiosClient().get(
                `${process.env.REACT_APP_COMMON_API}/email/email-templates?type=${encodeURIComponent(type)}`
            );
            const tmpl = response?.data?.template;
            if (tmpl) {
                setSubject(applyTemplatePlaceholders(tmpl.subject || ''));
                setMessage(applyTemplatePlaceholders(tmpl.template || ''));
                setErrors({});
            }
        } catch (err) {
            console.error('Failed to load template:', err);
            toast.error('Failed to load template. Please try again.');
        }
    };

    const findUnresolvedPlaceholders = (...values) => {
        const tokens = new Set();
        const bracketPattern = /\[[^\]\n]{2,120}\]/g;
        const hashPattern = /#[A-Z][A-Z0-9_]*(?:#|\b)/gi;
        const ignoredTokens = new Set(['#DESCRIBE_REQUIREMENT#', '#DESCRIBE_REQUIREMENT']);

        values.filter(Boolean).forEach((value) => {
            const text = `${value || ''}`;
            const bracketMatches = text.match(bracketPattern) || [];
            const hashMatches = text.match(hashPattern) || [];

            bracketMatches.forEach((match) => tokens.add(match.trim()));
            hashMatches.forEach((match) => tokens.add(match.trim()));
        });

        return Array.from(tokens).filter((token) => !ignoredTokens.has(token.toUpperCase()));
    };

    const validate = () => {
        const errs = {};
        if (!subject.trim()) errs.subject = 'Subject is required';
        if (getPlainTextLength(message) < 20) errs.message = 'Message must be at least 20 characters';
        const unresolvedPlaceholders = findUnresolvedPlaceholders(subject, message);
        if (unresolvedPlaceholders.length > 0) {
            errs.message = `Please replace these placeholders before sending: ${unresolvedPlaceholders.join(', ')}`;
        }
        setErrors(errs);

        if (unresolvedPlaceholders.length > 0) {
            toast.error(`Please replace these placeholders before sending: ${unresolvedPlaceholders.join(', ')}`);
        }

        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        if (isAnyUploading) {
            toast.warn('Please wait for all attachments to finish uploading.');
            return;
        }
        const failedUploads = attachments.filter((a) => a.error);
        if (failedUploads.length > 0) {
            toast.error('Please remove failed attachments before sending.');
            return;
        }

        setIsSubmitting(true);
        try {
            await axiosClient().post(`${process.env.REACT_APP_COMMON_API}/email/send-email`, {
                providerId: activeProvider?.providerId || '',
                to: activeProvider?.contactEmailId || '',
                subject: subject.trim(),
                message: message.trim(),
                ...(selectedTemplateType && { templateType: selectedTemplateType }),
                attachmentUrls,
            });

            toast.success('Email sent successfully!');
            resetForm();
            setShow(false);
        } catch (err) {
            console.error('Send email error:', err);
            const errorMessages = err?.response?.data?.errors;
            if (Array.isArray(errorMessages) && errorMessages.length > 0) {
                toast.error(errorMessages.join(', '));
            } else {
                toast.error('Failed to send email. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAttachmentChange = async (e) => {
        const files = Array.from(e.target.files);
        e.target.value = '';
        if (!files.length) return;

        // Add pending entries immediately so the user sees progress
        const newEntries = files.map((file) => ({
            id: `${Date.now()}_${Math.random()}`,
            name: file.name,
            url: null,
            uploading: true,
            error: null,
        }));
        setAttachments((prev) => [...prev, ...newEntries]);

        // Upload each file and update its entry in state
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const entryId = newEntries[i].id;
            try {
                const uploadForm = new FormData();
                uploadForm.append('pdf_doc', file);
                const uploadResponse = await axiosClient().post(
                    `${process.env.REACT_APP_COMMON_API}/upload/get-attached`,
                    uploadForm,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );
                const s3Url = uploadResponse?.data?.link;
                if (!s3Url) throw new Error('No URL returned from server');
                setAttachments((prev) =>
                    prev.map((a) => (a.id === entryId ? { ...a, url: s3Url, uploading: false } : a))
                );
            } catch (err) {
                console.error(`Failed to upload ${file.name}:`, err);
                setAttachments((prev) =>
                    prev.map((a) =>
                        a.id === entryId
                            ? { ...a, uploading: false, error: 'Upload failed' }
                            : a
                    )
                );
            }
        }
    };

    const removeAttachment = (id) => {
        setAttachments((prev) => prev.filter((a) => a.id !== id));
    };

    if (isSuperAdmin) {
        return null;
    }

    return (
        <>
            <a className={`${className} ${getActionElementClass()}`} onClick={handleShow} href="javascript:;">
                {btnTitle === 'Send Email' ? (
                    <span className="ms-1">
                        <i className="icon-mail me-1"></i> {btnTitle}
                    </span>
                ) : iconMain ? (
                    <i className="icon-mail"></i>
                ) : (
                    <span>{btnTitle}</span>
                )}
            </a>

            <Modal show={show} onHide={handleClose} size="lg" centered>
                <Modal.Body className="p-4 p-md-5">
                    <h5 className="font-30 font-600 mb-4">Compose Email</h5>

                    <a
                        href="javascript:;"
                        className="position-absolute top-20 right-20 m-0 text-secondary font-30"
                        onClick={handleClose}
                        aria-label="Close">
                        <i className="bi bi-x-circle-fill"></i>
                    </a>

                    <form className="needs-validation" noValidate onSubmit={handleSubmit}>
                        <div className="row g-4">
                            {/* Template selector */}
                            <div className="col-12">
                                <label className="form-label">Select Email Template</label>
                                <select
                                    className="form-select"
                                    value={selectedTemplateType}
                                    onChange={handleTemplateChange}
                                    disabled={isLoadingTemplates}>
                                    <option value="">{isLoadingTemplates ? 'Loading templates…' : 'Choose a template (optional)'}</option>
                                    {templates.map((t) => {
                                        const code = t.code || t.type;
                                        const label = code ? code.replace(/_/g, ' ') : '';
                                        return (
                                            <option key={code} value={code}>
                                                {label}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>

                            {/* To (read-only) */}
                            <div className="col-12">
                                <label className="form-label">To</label>
                                <input type="text" className="form-control bg-light" value={activeProvider?.alumniName || ''} readOnly />
                                <input
                                    type="text"
                                    className="form-control bg-light mt-2"
                                    value={activeProvider?.contactEmailId || ''}
                                    readOnly
                                    placeholder="No contact email available"
                                />
                                {isLoadingProvider && (
                                    <div className="form-text text-muted">
                                        Loading provider contact details...
                                    </div>
                                )}
                                {!isLoadingProvider && !activeProvider?.contactEmailId && (
                                    <div className="form-text text-warning">
                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                        No contact email on record for this provider.
                                    </div>
                                )}
                            </div>

                            {/* Subject */}
                            <div className="col-12">
                                <label className="form-label">
                                    Subject <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.subject ? 'is-invalid' : ''}`}
                                    placeholder="Enter subject"
                                    value={subject}
                                    onChange={(e) => {
                                        setSubject(e.target.value);
                                        if (errors.subject) setErrors((p) => ({ ...p, subject: '' }));
                                    }}
                                />
                                {errors.subject && <div className="invalid-feedback">{errors.subject}</div>}
                            </div>

                            {/* Message */}
                            <div className="col-12">
                                <label className="form-label">
                                    Message <span className="text-danger">*</span>
                                </label>
                                <div className={errors.message ? 'is-invalid' : ''}>
                                    <ReactQuill
                                        theme="snow"
                                        value={message}
                                        onChange={(value) => {
                                            setMessage(value);
                                            if (errors.message) setErrors((p) => ({ ...p, message: '' }));
                                        }}
                                        placeholder="Write your message…"
                                        style={{ minHeight: '180px' }}
                                        modules={{
                                            toolbar: [
                                                ['bold', 'italic', 'underline'],
                                                [{ list: 'ordered' }, { list: 'bullet' }],
                                                ['clean'],
                                            ],
                                        }}
                                    />
                                </div>
                                {errors.message && (
                                    <div className="text-danger" style={{ fontSize: '0.875em', marginTop: '0.25rem' }}>
                                        {errors.message}
                                    </div>
                                )}
                                <div className="form-text text-muted">{getPlainTextLength(message)} characters (min. 20)</div>
                            </div>

                            {/* Attachments */}
                            <div className="col-12">
                                <label className="form-label">Attachments</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    multiple
                                    disabled={isSubmitting}
                                    onChange={handleAttachmentChange}
                                />
                                {attachments.length > 0 && (
                                    <ul className="list-unstyled mt-2 mb-0">
                                        {attachments.map((att) => (
                                            <li key={att.id} className="d-flex align-items-center gap-2 font-14 mt-1">
                                                {att.uploading ? (
                                                    <span
                                                        className="spinner-border spinner-border-sm text-secondary"
                                                        role="status"
                                                        aria-hidden="true"></span>
                                                ) : att.error ? (
                                                    <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                                ) : (
                                                    <i className="bi bi-paperclip text-success"></i>
                                                )}
                                                <span className={`text-truncate flex-grow-1 ${att.error ? 'text-danger' : ''}`}>
                                                    {att.name}
                                                    {att.error && <span className="ms-1 font-12">({att.error})</span>}
                                                </span>
                                                {!att.uploading && (
                                                    <a
                                                        href="javascript:;"
                                                        className="text-danger"
                                                        onClick={() => removeAttachment(att.id)}
                                                        aria-label="Remove attachment">
                                                        <i className="bi bi-x-lg"></i>
                                                    </a>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="col-12">
                                <div className="d-flex gap-3">
                                    <Button type="submit" className="btn-green rounded-1" disabled={isSubmitting || isAnyUploading}>
                                        {isSubmitting ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"></span>
                                                Sending…
                                            </>
                                        ) : (
                                            <span>Send Email</span>
                                        )}
                                    </Button>
                                    <Button variant="outline-secondary" type="button" onClick={handleClose} disabled={isSubmitting}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default ComposeEmailModal;
